import { useMemo } from 'react';

// Canonical password rules — single source of truth for the entire frontend.
// The Python CFs in python-functions/main.py mirror this regex; keep them in sync.
// NOTE: apostrophe (') is intentionally excluded — Firebase Admin SDK's
// auth.update_user() rejects it as not meeting PASSWORD_DOES_NOT_MEET_REQUIREMENTS
// even though the client SDK (confirmPasswordReset / updatePassword) accepts it.
export const passwordRules = {
  minLength: 12,
  number: /[0-9]/,
  symbol: /[!@#$%^&*(),.?":{}|<>_+=~`/\\[\];-]/,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/
};

// Returns both per-rule booleans (for live PasswordRequirements checkmarks) and
// an errors string[] (for submit-time guards). Callers destructure what they need.
export function validatePasswordString(password) {
  const checks = [
    { key: 'hasMinLength', pass: password.length >= passwordRules.minLength, msg: 'Password must be at least 12 characters long' },
    { key: 'hasNumber',    pass: passwordRules.number.test(password),        msg: 'Password must contain at least one number' },
    { key: 'hasSymbol',    pass: passwordRules.symbol.test(password),        msg: 'Password must contain at least one special character' },
    { key: 'hasUppercase', pass: passwordRules.uppercase.test(password),     msg: 'Password must contain at least one uppercase letter' },
    { key: 'hasLowercase', pass: passwordRules.lowercase.test(password),     msg: 'Password must contain at least one lowercase letter' },
  ];

  const result = { errors: [] };
  for (const { key, pass, msg } of checks) {
    result[key] = pass;
    if (!pass) result.errors.push(msg);
  }
  result.isValid = result.errors.length === 0;
  return result;
}

export default function usePasswordValidation(password, onValidityChange) {
  const result = useMemo(() => validatePasswordString(password || ''), [password]);

  // Notify parent on changes to validity
  if (typeof onValidityChange === 'function') {
    // This runs on render; callers should memoize handler or accept frequent calls
    onValidityChange(result.isValid);
  }

  return result;
}


